import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DataManager from '../components/DataManager';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import PrintReportModal from '../components/PrintReportModal';
import { 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
  Printer
} from 'lucide-react';

const Inventory = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    quantity: 0,
    unit: 'قطعة',
    cost_per_unit: 0,
    reorder_level: 10,
    purchase_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    notes: '',
    days_per_unit: null,  // كم يوم تستهلك الوحدة الواحدة
    alert_at_units: 2,    // التنبيه عند كم وحدة متبقية
    is_permanent: false,  // عدّة دائمة (طاولة، مكينة، إلخ.)
  });
  const [showPrintModal, setShowPrintModal] = useState(false);

  // حساب تاريخ التنبيه (أقرب يوم سبت)
  const calculateAlertDate = (quantity, alertAtUnits, daysPerUnit) => {
    if (!daysPerUnit || daysPerUnit <= 0) return null;
    
    const unitsToConsume = quantity - alertAtUnits;
    if (unitsToConsume <= 0) return new Date(); // تنبيه فوري
    
    const daysUntilAlert = unitsToConsume * daysPerUnit;
    const alertDate = new Date();
    alertDate.setDate(alertDate.getDate() + daysUntilAlert);
    
    // إيجاد أقرب يوم سبت
    const dayOfWeek = alertDate.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    if (daysUntilSaturday === 0 && alertDate > new Date()) {
      // إذا كان اليوم سبت والتاريخ في المستقبل، ابقِ عليه
    } else if (daysUntilSaturday > 0) {
      alertDate.setDate(alertDate.getDate() + daysUntilSaturday);
    } else {
      alertDate.setDate(alertDate.getDate() + 7); // السبت القادم
    }
    
    return alertDate;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-EG', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get('/inventory/with-days');
      setItems(res.data);
    } catch (error) {
      toast.error('فشل تحميل المخزون');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, formData);
        toast.success('تم تحديث المادة');
      } else {
        await api.post('/inventory', formData);
        toast.success('تم إضافة المادة');
      }
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('فشل حفظ المادة');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity || 0,
      unit: item.unit || 'قطعة',
      cost_per_unit: item.cost_per_unit || item.unit_price || 0,
      reorder_level: item.reorder_level || item.min_stock || 10,
      purchase_date: item.purchase_date || '',
      expiry_date: item.expiry_date || '',
      notes: item.notes || '',
      days_per_unit: item.days_per_unit || null,
      alert_at_units: item.alert_at_units || 2,
      is_permanent: !!item.is_permanent,
    });
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المادة؟')) return;
    
    try {
      await api.delete(`/inventory/${itemId}`);
      toast.success('تم حذف المادة');
      fetchItems();
    } catch (error) {
      toast.error('فشل حذف المادة');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      quantity: 0,
      unit: 'قطعة',
      cost_per_unit: 0,
      reorder_level: 10,
      purchase_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      notes: '',
      days_per_unit: null,
      alert_at_units: 2,
      is_permanent: false,
    });
  };

  const getDaysStatus = (days) => {
    if (days === null || days === undefined) return null;
    if (days < 0) return { color: 'text-red-500 bg-red-500/20', label: 'منتهي', icon: AlertTriangle };
    if (days <= 7) return { color: 'text-red-500 bg-red-500/20', label: `${days} يوم`, icon: AlertTriangle };
    if (days <= 30) return { color: 'text-yellow-500 bg-yellow-500/20', label: `${days} يوم`, icon: Clock };
    return { color: 'text-green-500 bg-green-500/20', label: `${days} يوم`, icon: CheckCircle };
  };

  const units = ['قطعة', 'علبة', 'زجاجة', 'كيلو', 'لتر', 'متر', 'حبة'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('inventory')}</h1>
          <p className="text-muted-foreground">إدارة المخزون والمواد</p>
        </div>
        <div className="flex gap-3">
          <DataManager
            collectionName="inventory"
            collectionLabel="المخزون"
            items={items}
            onRefresh={fetchItems}
            idField="id"
            nameField="name"
            isAdmin={isAdmin()}
          />
          <Button 
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowForm(true);
            }}
          className="bg-primary text-primary-foreground"
        >
          <Plus className="w-4 h-4 me-2" />
          إضافة مادة
        </Button>
        
        {/* زر طباعة التقرير */}
        <Button 
          onClick={() => setShowPrintModal(true)} 
          variant="outline" 
          className="border-primary/50 text-primary hover:bg-primary/10"
        >
          <Printer className="w-4 h-4 me-2" />
          طباعة تقرير
        </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">إجمالي المواد</p>
              <p className="text-2xl font-bold">{items.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">تحتاج إعادة طلب</p>
              <p className="text-2xl font-bold">
                {items.filter(i => i.quantity <= (i.reorder_level || 10)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">قريبة من النفاذ</p>
              <p className="text-2xl font-bold">
                {items.filter(i => i.days_remaining !== null && i.days_remaining <= 30 && i.days_remaining >= 0).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">القيمة الإجمالية</p>
              <p className="text-2xl font-bold">
                <CurrencyDisplay amount={items.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cost_per_unit || i.unit_price || 0)), 0)} />
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingItem ? 'تعديل المادة' : 'إضافة مادة جديدة'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">اسم المادة</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-secondary/50"
                  placeholder="مثال: شامبو، جل، ..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">الكمية</label>
                  <Input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                    required
                    className="bg-secondary/50"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الوحدة</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full bg-secondary/50 border border-white/10 rounded-md p-2"
                  >
                    {units.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2"><CurrencyLabel label="سعر الوحدة" /></label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) || 0 })}
                    required
                    className="bg-secondary/50"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">حد إعادة الطلب</label>
                  <Input
                    type="number"
                    value={formData.reorder_level}
                    onChange={(e) => setFormData({ ...formData, reorder_level: parseInt(e.target.value) || 10 })}
                    className="bg-secondary/50"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline me-1" />
                    تاريخ الشراء
                  </label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="bg-secondary/50"
                  />
                </div>
                <div className={formData.is_permanent ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium mb-2">
                    <Clock className="w-4 h-4 inline me-1" />
                    تاريخ انتهاء الكمية
                  </label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="bg-secondary/50"
                    disabled={formData.is_permanent}
                  />
                </div>
              </div>

              {/* Permanent toggle: furniture, machines, fixtures that never expire */}
              <label
                className="flex items-center gap-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 cursor-pointer hover:bg-emerald-500/10 transition-colors"
                data-testid="permanent-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={!!formData.is_permanent}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setFormData((s) => ({
                      ...s,
                      is_permanent: v,
                      // Clear expiry + smart-alert fields when toggled ON
                      expiry_date: v ? '' : s.expiry_date,
                      days_per_unit: v ? null : s.days_per_unit,
                    }));
                  }}
                  className="w-5 h-5 accent-emerald-500"
                  data-testid="permanent-checkbox"
                />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-300 flex items-center gap-2">
                    🛠️ عُدّة دائمة (لا تنتهي)
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    اختر هذا الخيار للطاولات، مكاين الحلاقة، الكراسي، المرايا، وكل عُدّة لا تستهلك. لن يتم احتساب تاريخ انتهاء أو تنبيه ذكي.
                  </p>
                </div>
              </label>

              {/* حقول التنبيه الذكي */}
              {!formData.is_permanent && (
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 space-y-4">
                <h3 className="font-bold text-primary flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  نظام التنبيه الذكي للمخزون
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      كم يوم تستهلك الوحدة الواحدة؟
                    </label>
                    <Input
                      type="number"
                      value={formData.days_per_unit || ''}
                      onChange={(e) => setFormData({ ...formData, days_per_unit: e.target.value ? parseInt(e.target.value) : null })}
                      className="bg-secondary/50"
                      min="1"
                      placeholder="مثال: 10 أيام"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      مثال: إذا كانت العلبة تكفي 10 أيام
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      تنبيهي عند كم {formData.unit} متبقية؟
                    </label>
                    <Input
                      type="number"
                      value={formData.alert_at_units}
                      onChange={(e) => setFormData({ ...formData, alert_at_units: parseInt(e.target.value) || 2 })}
                      className="bg-secondary/50"
                      min="1"
                      placeholder="مثال: 2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      سيتم التنبيه قبل وصول الكمية لهذا الحد
                    </p>
                  </div>
                </div>

                {/* عرض تاريخ التنبيه المحسوب */}
                {formData.days_per_unit > 0 && formData.quantity > 0 && (
                  <div className="bg-card/50 rounded-lg p-3 border border-white/10">
                    <p className="text-sm text-muted-foreground mb-1">التنبيه المتوقع:</p>
                    <p className="font-bold text-primary">
                      {(() => {
                        const alertDate = calculateAlertDate(
                          formData.quantity, 
                          formData.alert_at_units, 
                          formData.days_per_unit
                        );
                        if (!alertDate) return '-';
                        const today = new Date();
                        const diffDays = Math.ceil((alertDate - today) / (1000 * 60 * 60 * 24));
                        return `${formatDate(alertDate)} (بعد ${diffDays} يوم)`;
                      })()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      * التنبيه سيكون يوم السبت مع التقرير الأسبوعي
                    </p>
                  </div>
                )}
              </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">ملاحظات</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="ملاحظات إضافية..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="flex-1 bg-primary text-primary-foreground">
                  {editingItem ? 'تحديث' : 'إضافة'}
                </Button>
                <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="flex-1">
                  إلغاء
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="font-semibold">جدول المخزون</h3>
        </div>
        
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">لا توجد مواد في المخزون</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="p-4 text-start">المادة</th>
                  <th className="p-4 text-start">الكمية</th>
                  <th className="p-4 text-start">سعر الوحدة</th>
                  <th className="p-4 text-start">القيمة</th>
                  <th className="p-4 text-start">أضيف بواسطة</th>
                  <th className="p-4 text-start">استهلاك الوحدة</th>
                  <th className="p-4 text-start">موعد التنبيه</th>
                  <th className="p-4 text-start">الحالة</th>
                  <th className="p-4 text-start">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const daysStatus = getDaysStatus(item.days_remaining);
                  const needsReorder = item.quantity <= (item.reorder_level || 10);
                  const hasAlert = item.days_per_unit && item.days_per_unit > 0;
                  const alertSoon = item.days_until_alert !== null && item.days_until_alert <= 14;
                  
                  return (
                    <tr key={item.id} className="border-t border-white/5 hover:bg-secondary/20">
                      <td className="p-4">
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {item.name}
                            {item.is_permanent && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-bold"
                                title="عُدّة دائمة - لا تنتهي"
                                data-testid={`permanent-badge-${item.id}`}
                              >
                                🛠️ دائم
                              </span>
                            )}
                          </p>
                          {item.notes && (
                            <p className="text-xs text-muted-foreground">{item.notes}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={needsReorder && !item.is_permanent ? 'text-red-500 font-bold' : ''}>
                          {item.quantity} {item.unit}
                        </span>
                      </td>
                      <td className="p-4"><CurrencyDisplay amount={item.cost_per_unit || item.unit_price || 0} /></td>
                      <td className="p-4 font-semibold">
                        <CurrencyDisplay amount={(item.quantity || 0) * (item.cost_per_unit || item.unit_price || 0)} />
                      </td>
                      <td className="p-4">
                        <span className="text-primary text-sm">
                          {item.added_by_name || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        {item.is_permanent ? (
                          <span className="text-emerald-400 text-xs">— لا تنتهي —</span>
                        ) : hasAlert ? (
                          <div className="text-sm">
                            <p>{item.days_per_unit} يوم/{item.unit}</p>
                            <p className="text-xs text-muted-foreground">
                              تنبيه عند {item.alert_at_units} {item.unit}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {item.is_permanent ? (
                          <span className="text-emerald-400 text-xs">—</span>
                        ) : hasAlert && item.alert_date ? (
                          <div className={`text-sm ${alertSoon ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                            <p className="font-medium">{formatDate(item.alert_date)}</p>
                            <p className="text-xs">
                              {item.days_until_alert <= 0 ? (
                                <span className="text-red-500 font-bold">⚠️ تنبيه الآن!</span>
                              ) : (
                                `بعد ${item.days_until_alert} يوم`
                              )}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {needsReorder || (item.days_until_alert !== null && item.days_until_alert <= 0) ? (
                          <span className="bg-red-500/20 text-red-500 px-3 py-1 rounded-full text-sm">
                            يحتاج طلب
                          </span>
                        ) : alertSoon ? (
                          <span className="bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full text-sm">
                            قريباً
                          </span>
                        ) : (
                          <span className="bg-green-500/20 text-green-500 px-3 py-1 rounded-full text-sm">
                            متوفر
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(item)}
                            className="hover:bg-primary/20"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {isAdmin() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item.id)}
                              className="hover:bg-red-500/20 text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* نافذة طباعة التقرير */}
      <PrintReportModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        reportType="inventory"
        isAdmin={isAdmin()}
      />
    </div>
  );
};

export default Inventory;
